import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultMeta = {
  page: 1,
  page_size: 10,
  total_items: 0,
  total_pages: 0
};

const defaultQueryParams = {
  page: 1,
  pageSize: 10,
  search: '',
  status: 'ALL',
  quotaFilter: 'ALL',
  sortBy: 'created_datetime',
  sortDirection: 'desc'
};

const initialState = {
  items: [],
  meta: defaultMeta,
  loading: false,
  error: null,
  lastQuery: defaultQueryParams,
  detail: null,
  detailStatus: 'idle',
  detailError: null,
  savingStatus: 'idle',
  savingError: null,
  statusUpdates: {},
  exportStatus: 'idle',
  exportError: null,
  statusCounts: {
    ALL: 0,
    ACTIVE: 0,
    INACTIVE: 0
  },
  secretGenerationStatus: 'idle',
  secretGenerationError: null,
  generatedSecret: null
};

const formatDateTime = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const pad = (number) => String(number).padStart(2, '0');

  const year = parsed.getFullYear();
  const month = pad(parsed.getMonth() + 1);
  const day = pad(parsed.getDate());
  const hours = pad(parsed.getHours());
  const minutes = pad(parsed.getMinutes());

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const transformAppSource = (item) => ({
  ...item,
  callback_url: item.callback_url || '',
  quota: Number(item.quota ?? 0),
  quota_usage: Number(item.quota_usage ?? 0),
  quota_type: item.quota_type || 'FARE',
  start_datetime: item.start_datetime ? formatDateTime(item.start_datetime) : null,
  end_datetime: item.end_datetime ? formatDateTime(item.end_datetime) : null,
  created_datetime: formatDateTime(item.created_datetime),
  updated_datetime: item.updated_datetime ? formatDateTime(item.updated_datetime) : null
});

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';

    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const buildQueryString = (params = {}) => {
  const merged = {
    ...defaultQueryParams,
    ...params
  };
  const { page, pageSize, search, status, quotaFilter, sortBy, sortDirection } = merged;

  const query = new URLSearchParams();

  if (page !== null && page !== undefined) {
    query.set('page', page);
  }

  if (pageSize !== null && pageSize !== undefined) {
    query.set('page_size', pageSize);
  }

  if (search) {
    query.set('search', search);
  }

  if (status && status !== 'ALL') {
    query.set('status', status);
  }

  if (quotaFilter && quotaFilter !== 'ALL') {
    query.set('quota_filter', quotaFilter);
  }

  if (sortBy) {
    query.set('sort_by', sortBy);
  }

  if (sortDirection) {
    query.set('sort_direction', sortDirection);
  }

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

const normalizeQueryParams = (params = {}) => ({
  page: params.page ?? defaultQueryParams.page,
  pageSize: params.pageSize ?? defaultQueryParams.pageSize,
  search: params.search ?? defaultQueryParams.search,
  status: params.status ?? defaultQueryParams.status,
  quotaFilter: params.quotaFilter ?? defaultQueryParams.quotaFilter,
  sortBy: params.sortBy ?? defaultQueryParams.sortBy,
  sortDirection: params.sortDirection ?? defaultQueryParams.sortDirection
});

const buildAuthHeaders = (getState, additional = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...additional
  };

  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

export const fetchAppSources = createAsyncThunk(
  'appSources/fetchAppSources',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const response = await fetch(`${API_BASE_URL}/api/app-sources${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const items = Array.isArray(data?.data) ? data.data.map(transformAppSource) : [];
      const meta = data?.meta
        ? {
            page: Number(data.meta.page ?? defaultMeta.page),
            page_size: Number(data.meta.page_size ?? defaultMeta.page_size),
            total_items: Number(data.meta.total_items ?? defaultMeta.total_items),
            total_pages: Number(data.meta.total_pages ?? defaultMeta.total_pages)
          }
        : defaultMeta;

      const statusCounts = (() => {
        const raw =
          data?.meta?.status_counts ||
          data?.meta?.statusCounts ||
          data?.meta?.status_summary ||
          data?.status_counts ||
          null;

        if (raw && typeof raw === 'object') {
          return {
            ALL:
              Number(
                raw.ALL ?? raw.all ?? raw.total ?? raw.TOTAL ?? data?.meta?.total_items
              ) || 0,
            ACTIVE: Number(raw.ACTIVE ?? raw.active ?? raw.ACT ?? raw['ACTIVE_COUNT']) || 0,
            INACTIVE:
              Number(raw.INACTIVE ?? raw.inactive ?? raw.INACT ?? raw['INACTIVE_COUNT']) || 0
          };
        }

        const shouldComputeFromItems =
          !params?.status || params.status === 'ALL' || params.status === 'all';

        if (shouldComputeFromItems) {
          return items.reduce(
            (acc, item) => {
              acc.ALL += 1;
              if (item.status === 'ACTIVE') acc.ACTIVE += 1;
              if (item.status === 'INACTIVE') acc.INACTIVE += 1;
              return acc;
            },
            { ALL: 0, ACTIVE: 0, INACTIVE: 0 }
          );
        }

        return null;
      })();

      return {
        items,
        meta,
        query: normalizeQueryParams(params),
        statusCounts
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch app sources.');
    }
  }
);

export const fetchAppSourceById = createAsyncThunk(
  'appSources/fetchAppSourceById',
  async (id, { getState, dispatch, rejectWithValue }) => {
    try {
      if (id === null || id === undefined) {
        throw new Error('App source ID is required.');
      }

      const response = await fetch(`${API_BASE_URL}/api/app-sources/${id}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);

      return transformAppSource(data);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load app source.');
    }
  }
);

export const createAppSource = createAsyncThunk(
  'appSources/createAppSource',
  async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/app-sources`, {
        method: 'POST',
        headers: buildAuthHeaders(getState),
        body: JSON.stringify(payload)
      });

      const data = await handleApiResponse(response, dispatch);
      if (Number(data?.resultCode) !== 200) {
        let message = data?.message;

        if (Number(data?.resultCode) === 409) {
          message = 'Name already exists.';
        }
        return rejectWithValue(message);
      }      

      return transformAppSource(data);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create app source.');
    }
  }
);

export const updateAppSource = createAsyncThunk(
  'appSources/updateAppSource',
  async ({ id, ...payload }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (id === null || id === undefined) {
        throw new Error('App source ID is required.');
      }

      const response = await fetch(`${API_BASE_URL}/api/app-sources/${id}`, {
        method: 'PUT',
        headers: buildAuthHeaders(getState),
        body: JSON.stringify(payload)
      });

      const data = await handleApiResponse(response, dispatch);
      if (Number(data?.resultCode) === 409) {
        const message =
          data?.resultDesc || data?.resultMessage || data?.message || 'Name already exists.';
        return rejectWithValue(message);
      }

      return transformAppSource(data);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update app source.');
    }
  }
);

export const changeAppSourceStatus = createAsyncThunk(
  'appSources/changeAppSourceStatus',
  async ({ id, status }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (id === null || id === undefined) {
        throw new Error('App source ID is required.');
      }

      const response = await fetch(`${API_BASE_URL}/api/app-sources/${id}/status`, {
        method: 'POST',
        headers: buildAuthHeaders(getState),
        body: JSON.stringify({ status })
      });

      const data = await handleApiResponse(response, dispatch);

      return {
        id: Number(data?.id ?? id),
        status: data?.status ?? status,
        updated_datetime: formatDateTime(data?.updated_datetime)
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update status.');
    }
  }
);

export const generateAppSourceSecret = createAsyncThunk(
  'appSources/generateAppSourceSecret',
  async (length = 64, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/app-sources/generate-secret`, {
        method: 'POST',
        headers: buildAuthHeaders(getState),
        body: JSON.stringify({ length })
      });

      const data = await handleApiResponse(response, dispatch);

      if (!data?.secret_key) {
        throw new Error('Secret key not returned by server.');
      }

      return data.secret_key;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to generate secret key.');
    }
  }
);

export const exportAppSources = createAsyncThunk(
  'appSources/exportAppSources',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const headers = buildAuthHeaders(getState, {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
      });

      const response = await fetch(`${API_BASE_URL}/api/app-sources/export${queryString}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const errorText = await response.text();
        throw new Error(errorText || response.statusText || 'Failed to export app sources.');
      }

      const blob = await response.blob();

      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const filenameMatch = disposition.match(/filename="?(.+?)"?$/i);
        const filename = filenameMatch ? filenameMatch[1] : 'app-sources.xlsx';

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to export app sources.');
    }
  }
);

const appSourcesSlice = createSlice({
  name: 'appSources',
  initialState,
  reducers: {
    clearAppSourceDetail: (state) => {
      state.detail = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.secretGenerationStatus = 'idle';
      state.secretGenerationError = null;
      state.generatedSecret = null;
    },
    resetSaveState: (state) => {
      state.savingStatus = 'idle';
      state.savingError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppSources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppSources.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.lastQuery = action.payload.query;
        state.error = null;
        if (action.payload.statusCounts) {
          state.statusCounts = action.payload.statusCounts;
        } else if (
          !action.payload.query?.status ||
          action.payload.query?.status === 'ALL' ||
          action.payload.query?.status === 'all'
        ) {
          const counts = action.payload.items.reduce(
            (acc, item) => {
              acc.ALL += 1;
              if (item.status === 'ACTIVE') acc.ACTIVE += 1;
              if (item.status === 'INACTIVE') acc.INACTIVE += 1;
              return acc;
            },
            { ALL: 0, ACTIVE: 0, INACTIVE: 0 }
          );
          state.statusCounts = counts;
        }
      })
      .addCase(fetchAppSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch app sources.';
      })
      .addCase(fetchAppSourceById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchAppSourceById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.detail = action.payload;
        state.detailError = null;
      })
      .addCase(fetchAppSourceById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload || 'Failed to load app source.';
      })
      .addCase(createAppSource.pending, (state) => {
        state.savingStatus = 'loading';
        state.savingError = null;
      })
      .addCase(createAppSource.fulfilled, (state, action) => {
        state.savingStatus = 'succeeded';
        state.savingError = null;
        if (action.payload) {
          const existingIndex = state.items.findIndex((item) => item.id === action.payload.id);
          if (existingIndex !== -1) {
            state.items[existingIndex] = action.payload;
          } else {
            state.items.unshift(action.payload);
          }
        }
      })
      .addCase(createAppSource.rejected, (state, action) => {
        state.savingStatus = 'failed';
        state.savingError = action.payload || 'Failed to create app source.';
      })
      .addCase(updateAppSource.pending, (state) => {
        state.savingStatus = 'loading';
        state.savingError = null;
      })
      .addCase(updateAppSource.fulfilled, (state, action) => {
        state.savingStatus = 'succeeded';
        state.savingError = null;

        if (action.payload) {
          const index = state.items.findIndex((item) => item.id === action.payload.id);
          if (index !== -1) {
            state.items[index] = action.payload;
          }

          if (state.detail?.id === action.payload.id) {
            state.detail = action.payload;
          }
        }
      })
      .addCase(updateAppSource.rejected, (state, action) => {
        state.savingStatus = 'failed';
        state.savingError = action.payload || 'Failed to update app source.';
      })
      .addCase(changeAppSourceStatus.pending, (state, action) => {
        const id = action.meta.arg?.id;
        if (id !== undefined && id !== null) {
          state.statusUpdates[id] = 'loading';
        }
      })
      .addCase(changeAppSourceStatus.fulfilled, (state, action) => {
        const { id, status, updated_datetime } = action.payload;
        const index = state.items.findIndex((item) => item.id === id);
        if (index !== -1) {
          state.items[index] = {
            ...state.items[index],
            status,
            updated_datetime
          };
        }
        state.statusUpdates[id] = 'succeeded';
      })
      .addCase(changeAppSourceStatus.rejected, (state, action) => {
        const id = action.meta.arg?.id;
        if (id !== undefined && id !== null) {
          state.statusUpdates[id] = 'failed';
        }
        state.error = action.payload || 'Failed to update status.';
      })
      .addCase(exportAppSources.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportAppSources.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
        state.exportError = null;
      })
      .addCase(exportAppSources.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to export app sources.';
      })
      .addCase(generateAppSourceSecret.pending, (state) => {
        state.secretGenerationStatus = 'loading';
        state.secretGenerationError = null;
      })
      .addCase(generateAppSourceSecret.fulfilled, (state, action) => {
        state.secretGenerationStatus = 'succeeded';
        state.secretGenerationError = null;
        state.generatedSecret = action.payload;
      })
      .addCase(generateAppSourceSecret.rejected, (state, action) => {
        state.secretGenerationStatus = 'failed';
        state.secretGenerationError = action.payload || 'Failed to generate secret key.';
      });
  }
});

export const { clearAppSourceDetail, resetSaveState } = appSourcesSlice.actions;

export default appSourcesSlice.reducer;
